import isObject from "@/util/is-object.mjs";

export default function digPath<P extends PropertyKey[], O extends object>(
  path: DigPath<P, O>,
  data: O,
): [unknown, PropertyKey] {
  let curr: unknown = data;

  const lastKey = path[path.length - 1];

  for (let i = 0; i < path.length - 1; i++) {
    curr = curr[path[i]];

    if (curr === undefined) break;
  }

  return [curr, lastKey];
}

// Helper type to get the value at a path in an object type
type PathValue<
  T,
  Path extends readonly PropertyKey[],
> = Path extends readonly never[]
  ? T
  : Path extends readonly [infer Head, ...infer Tail]
    ? Head extends keyof T
      ? Tail extends readonly PropertyKey[]
        ? PathValue<T[Head], Tail>
        : never
      : undefined
    : never;

// Helper type to get the parent object type at a path
type ParentAtPath<
  T,
  Path extends readonly PropertyKey[],
> = Path extends readonly []
  ? T
  : Path extends readonly [...infer Init, PropertyKey]
    ? Init extends readonly PropertyKey[]
      ? PathValue<T, Init>
      : never
    : never;

// Helper type to extract the last element of a tuple
type LastElement<T extends readonly PropertyKey[]> = T extends readonly [
  ...PropertyKey[],
  infer Last,
]
  ? Last
  : never;

/**
 * Deeply traverses an object following a path and returns the parent object and final key.
 * This is useful for safely accessing and modifying nested properties.
 *
 * @template P - The path array type
 * @template O - The source object type
 *
 * @param path - Array of property keys to traverse
 * @param data - The object to traverse
 *
 * @returns A tuple of [parent object, final key] where:
 *   - parent object: The deepest object that could be reached (may be undefined)
 *   - final key: The last key in the path
 *
 * @example
 * ```typescript
 * const obj = { a: { b: { c: 123 } } };
 * const [parent, key] = digPath(['a', 'b', 'c'] as const, obj);
 * // parent = { c: 123 }, key = 'c'
 * // parent[key] = 123
 *
 * const [parent2, key2] = digPath(['a', 'x', 'y'] as const, obj);
 * // parent2 = undefined, key2 = 'y'
 * // Because obj.a.x doesn't exist
 * ```
 */
export function typedDigPath<
  P extends readonly PropertyKey[],
  O extends object,
>(
  path: P & { length: number },
  data: O,
):
  | [parent: O, key: PropertyKey]
  | [parent: undefined, key: PropertyKey]
  | [parent: O, key: LastElement<P>]
  | [
      parent: ParentAtPath<O, P>,
      key: LastElement<P> extends PropertyKey ? LastElement<P> : PropertyKey,
    ] {
  // Early exit for empty paths
  if (path.length === 0) {
    return [data, ""];
  }

  // Early exit for single-element paths
  if (path.length === 1) {
    return [data, path[0]];
  }

  let current: unknown = data;
  const lastKey = path[path.length - 1];
  const pathLength = path.length - 1;

  // Traverse the path up to the second-to-last element
  for (let i = 0; i < pathLength; i++) {
    // Type guard to ensure we can access properties
    if (!isObject(current)) {
      return [undefined, lastKey];
    }

    const key = path[i];
    current = (current as Record<PropertyKey, unknown>)[key];
  }

  return [current as ParentAtPath<O, P>, lastKey];
}
